"use client";

import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

interface Slice {
  label: string;
  value: number;
  color?: string;
}

interface PieChartBlockProps {
  data: Slice[];
  title?: string;
  caption?: string;
}

const COLORS = [
  "#3B82F6", // nws-blue
  "#2563EB", // nws-blue-dark
  "#AED1FF", // nws-blue-light
  "#EEF975", // nws-yellow
  "#5C5C5C", // nws-grey-text
  "#DFDFDF", // nws-grey-medium
];

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: Slice }>;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-nws-grey-medium rounded-xl px-4 py-2 shadow-sm font-sans text-sm">
      <span className="font-semibold text-black">{d.label}</span>
      <span className="text-nws-grey-text ml-2">{d.value}%</span>
    </div>
  );
}

const RADIAN = Math.PI / 180;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderLabel(props: any) {
  const { cx, cy, midAngle, outerRadius, percent, label } = props;
  const radius = outerRadius + 28;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null;

  return (
    <text
      x={x}
      y={y}
      fill="#5C5C5C"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      className="text-xs font-sans"
    >
      {label} ({(percent * 100).toFixed(1)}%)
    </text>
  );
}

export default function PieChartBlock({
  data,
  title,
  caption,
}: PieChartBlockProps) {
  if (!data?.length) return null;

  return (
    <figure className="my-8">
      {title && (
        <p className="font-sans font-medium text-black text-center text-base lg:text-lg mb-4">
          {title}
        </p>
      )}
      <div className="w-full border border-nws-grey-medium rounded-2xl bg-white p-6">
        <ResponsiveContainer width="100%" height={340}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              outerRadius={110}
              innerRadius={48}
              dataKey="value"
              nameKey="label"
              label={renderLabel}
              labelLine={false}
              strokeWidth={2}
              stroke="#fff"
            >
              {data.map((entry, i) => (
                <Cell
                  key={entry.label}
                  fill={entry.color || COLORS[i % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {caption && (
        <figcaption className="text-center text-sm text-nws-grey-text mt-3 font-sans">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
